import type { Container } from 'pixi.js';
import { Signal } from 'typed-signals';

interface VariableValueText {
  type: 'text';
  name: string;
  value: string;
}

interface VariableValueTime {
  type: 'time';
  name: string;
  value: number;
}

interface VariableValueDisplayObject {
  type: 'displayObject',
  name: string;
  value: () => Container;
}

interface VariableValueCustom {
  type: 'custom',
  name: string;
  value: unknown;
}

export type VariableValue = VariableValueText | VariableValueTime | VariableValueDisplayObject | VariableValueCustom;

export class VariableBank {
  public onVariableSet= new Signal<(value: VariableValue) => void>();

  private readonly bank: Record<string, VariableValue> = {};

  private addToBank(name: string, value: VariableValue): void {
    this.bank[name] = value;
    this.onVariableSet.emit(value);
  }

  public setTimeVariable(name: string, value: number): void {
    this.addToBank(name, {
      name,
      type: 'time',
      value,
    });
  }

  public setTextVariable(name: string, value: string | number): void {
    this.addToBank(name, {
      name,
      type: 'text',
      value: `${value}`,
    });
  }

  public setDisplayObjectVariable(name: string, value: () => Container): void {
    this.addToBank(name, {
      name,
      type: 'displayObject',
      value,
    });
  }

  public setCustomVariable(name: string, value: VariableValueCustom): void {
    this.addToBank(name, value);
  }

  public getVariable(name: string): VariableValue | null {
    return this.bank[name] ?? null;
  }

  public getTextVariableValue(name: string, fallback: string): string {
    const variable = this.getVariable(name);
    if (!variable || variable.type !== 'text') return fallback;
    return variable.value;
  }
}
